/**
 * VocalTutoPage — onglet « Tuto » de /vocal.
 *
 * Page statique : explique au soignant le fonctionnement du mode vocal
 * (réveil + auto-écoute, couleurs de l'orbe, vocabulaire accepté).
 * Source : docs/meta/plan/UX-VOCALE-SPEC.md §2/§4/§6.
 * NB : le cercle a 6 états (VREC1) ; le tuto en montre 4 (off/listening/capturing/confirming).
 */

import { VoiceRecorder } from '@/shared/ds/patterns';

// Légende des 4 états réels de l'orbe (couleurs portées par le composant DS).
const ORB_STATES = [
  { state: 'off' as const, legende: 'Coupé — touchez le cercle pour activer' },
  { state: 'listening' as const, legende: 'À l\'écoute' },
  { state: 'capturing' as const, legende: 'Parlez — saisie en cours' },
  { state: 'confirming' as const, legende: 'Confirmez — "validation", "corrige" ou "annule"' },
];

// Vocabulaire déterministe accepté (§6).
const VOCAB = [
  { intention: 'Démarrer', icon: 'bi-play-circle', mots: '« saisi chambre N » (ex. « saisi chambre 101 »)' },
  { intention: 'Choisir un patient', icon: 'bi-person', mots: 'par numéro — « patient 2 »' },
  { intention: 'Choisir une action', icon: 'bi-list-ol', mots: 'un mot-clé — « prescriptions », « constantes », « transmission », « score », « refus », « relire » (ou un numéro)' },
  { intention: 'Valider', icon: 'bi-check-circle', mots: '« validation » (ou « valide »)' },
  { intention: 'Corriger', icon: 'bi-pencil', mots: '« corrige »' },
  { intention: 'Annuler', icon: 'bi-x-circle', mots: '« annule »' },
  { intention: 'Finir de parler', icon: 'bi-stop-circle', mots: '« stop » (ou silence)' },
];

export function VocalTutoPage() {
  return (
    <div className="vocal-doc">
      <h1 className="h4 fw-bold mb-3">Tuto — mode vocal</h1>
      <p className="text-muted mb-4">
        Le mode vocal permet une saisie mains-libres, au lit du patient : les
        mains restent sur le patient, les yeux aussi.
      </p>

      {/* Comment ça marche (§2) */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-header bg-transparent border-bottom py-3 d-flex align-items-center gap-2">
          <i className="bi bi-mic-fill text-primary" />
          <span className="fw-semibold">Comment ça marche</span>
        </div>
        <div className="card-body p-3">
          <div className="mb-3">
            <div className="fw-semibold small d-flex align-items-center gap-2 mb-1">
              <i className="bi bi-bell text-primary" /> Réveil
            </div>
            <p className="text-muted small mb-0 ms-4">
              On dit <span className="fw-semibold">« saisi »</span> une fois pour
              démarrer une saisie (ex. « saisi chambre 101 »).
            </p>
          </div>
          <div className="mb-3">
            <div className="fw-semibold small d-flex align-items-center gap-2 mb-1">
              <i className="bi bi-arrow-repeat text-primary" /> Auto-écoute
            </div>
            <p className="text-muted small mb-0 ms-4">
              Ensuite, après chaque réponse vocale, le micro se ré-ouvre tout
              seul — on répond <span className="fw-semibold">sans redire « saisi »</span>.
            </p>
          </div>
          <div>
            <div className="fw-semibold small d-flex align-items-center gap-2 mb-1">
              <i className="bi bi-stop-circle text-primary" /> Fin d'un tour
            </div>
            <p className="text-muted small mb-0 ms-4">
              On dit <span className="fw-semibold">« stop »</span> ou on fait une
              courte pause : le micro se coupe seul.
            </p>
          </div>
        </div>
      </div>

      {/* L'orbe — 4 états (§4) */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-header bg-transparent border-bottom py-3 d-flex align-items-center gap-2">
          <i className="bi bi-circle-half text-primary" />
          <span className="fw-semibold">L'orbe — 4 états</span>
        </div>
        <div className="card-body p-3">
          <p className="text-muted small mb-3">
            La couleur de l'orbe répond, au coup d'œil, à la question « qu'est-ce
            que je fais, là ? ».
          </p>
          <div className="row g-3 text-center">
            {ORB_STATES.map(({ state, legende }) => (
              <div key={state} className="col-6 col-md-3 d-flex flex-column align-items-center">
                <VoiceRecorder state={state} title={legende} />
                <span className="text-muted small mt-2">{legende}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Vocabulaire (§6) */}
      <div className="card border-0 shadow-sm">
        <div className="card-header bg-transparent border-bottom py-3 d-flex align-items-center gap-2">
          <i className="bi bi-chat-square-quote text-primary" />
          <span className="fw-semibold">Vocabulaire</span>
        </div>
        <ul className="list-group list-group-flush">
          {VOCAB.map(({ intention, icon, mots }) => (
            <li key={intention} className="list-group-item d-flex align-items-center gap-3">
              <i className={`bi ${icon} text-primary`} />
              <span className="fw-semibold small" style={{ minWidth: 180 }}>{intention}</span>
              <span className="text-muted small">{mots}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
